import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Image as ImageIcon, Copy, Trash2, ExternalLink, RotateCcw, Info } from 'lucide-react';
import { format } from 'date-fns';
import { platformLabel } from '@/lib/clipPlatformFormats';

const STATUS_STYLE = {
  draft: 'border-[#555E6E]/40 text-[#8A93A6]', ready: 'border-[#8A93A6]/40 text-[#8A93A6]',
  scheduled: 'border-[#3A7BFF]/40 text-[#3A7BFF]', publishing: 'border-amber-500/40 text-amber-400',
  published: 'border-emerald-500/40 text-emerald-400', failed: 'border-red-500/40 text-red-400',
};

/**
 * Schedule → Review / post detail (section 14). Metadata is read-only
 * here by design (section 14: "Do not recreate the Optimize editing
 * workflow inside Schedule") — only date/time can be adjusted; anything
 * else routes back to Optimize with the asset/platform preserved.
 */
export function SchedulePreviewPanel({ post, onEditDateTime, onDuplicate, onRemove, onOpenInOptimize, timezone }) {
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(post ? format(new Date(post.scheduledAt), "yyyy-MM-dd'T'HH:mm") : '');

  if (!post) return <p className="text-xs text-[#8A93A6] text-center py-6" data-testid="schedule-preview-empty">Select a post on the calendar or agenda to review it here.</p>;

  const save = () => { onEditDateTime(post.id, new Date(date).toISOString()); setEditing(false); };

  return (
    <div className="space-y-3" data-testid="schedule-preview-panel">
      <div className="aspect-video rounded-lg border border-white/10 bg-black/40 flex items-center justify-center overflow-hidden">
        {post.thumbnailUrl ? <img src={post.thumbnailUrl} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="h-5 w-5 text-[#555E6E]" />}
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-[#8A93A6]">{platformLabel(post.platform)}</span>
        <Badge variant="outline" className={STATUS_STYLE[post.status] || STATUS_STYLE.scheduled} data-testid="schedule-preview-status-badge">{post.status}{post.status === 'published' && post.mocked !== false ? ' (Mock)' : ''}</Badge>
      </div>

      <div>
        <p className="text-sm text-white">{post.title || 'Untitled'}</p>
        <p className="text-xs text-[#8A93A6] mt-1 line-clamp-3">{post.description}</p>
        {post.hashtags && <p className="text-[11px] text-[#555E6E] mt-1">{post.hashtags}</p>}
      </div>

      {post.status === 'failed' && post.failureReason && (
        <div className="p-2 rounded-lg border border-red-500/20 bg-red-500/5 flex items-start gap-1.5">
          <Info className="h-3 w-3 text-red-400 mt-0.5 shrink-0" />
          <p className="text-[11px] text-red-300">{post.failureReason}</p>
        </div>
      )}

      <div>
        <p className="text-[10px] text-[#555E6E]">Scheduled for ({timezone})</p>
        {editing ? (
          <div className="flex gap-1.5 mt-1">
            <Input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} className="h-7 text-xs bg-black/40 border-white/10" data-testid="schedule-preview-edit-datetime-input" />
            <Button size="sm" className="h-7 bg-[#3A7BFF]" onClick={save} data-testid="schedule-preview-save-datetime-button">Save</Button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} className="text-xs text-white hover:underline" data-testid="schedule-preview-edit-datetime-trigger">{format(new Date(post.scheduledAt), 'PPP p')}</button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 pt-1">
        <Button size="sm" variant="outline" className="border-white/10" onClick={() => onDuplicate(post.id)} data-testid="schedule-preview-duplicate-button"><Copy className="h-3.5 w-3.5 mr-1.5" />Duplicate</Button>
        <Button size="sm" variant="outline" className="border-red-500/30 text-red-400" onClick={() => onRemove(post.id)} data-testid="schedule-preview-remove-button"><Trash2 className="h-3.5 w-3.5 mr-1.5" />Remove</Button>
      </div>
      <Button size="sm" variant="ghost" className="w-full text-xs text-[#8A93A6] hover:text-white" onClick={() => onOpenInOptimize(post)} data-testid="schedule-preview-open-optimize-button">
        <ExternalLink className="h-3.5 w-3.5 mr-1.5" />Need to change the title or caption? Open in Optimize
      </Button>
      {post.status === 'failed' && (
        <Button size="sm" variant="outline" className="w-full border-amber-500/30 text-amber-300" onClick={() => onOpenInOptimize(post)} data-testid="schedule-preview-retry-button"><RotateCcw className="h-3.5 w-3.5 mr-1.5" />Retry from Optimize</Button>
      )}
    </div>
  );
}
